import { useLauncherStore } from "@/app/[tenantId]/[messengerId]/[hostConfig]/lib/client/contexts/LauncherStoreContext";
import useHasFooter from "@/lib/client/hooks/useHasFooter";
import type { MessengerThemeSettings } from "@deskpro-product/messenger-api-schemas/messenger/config/settings";
import { clsx } from "clsx";
import { usePermalink } from "@/lib/client/hooks/usePermalink";
import { getMessengerToken } from "@/lib/client/utils/config";

export default function Background({
  children,
  params,
}: {
  children: React.ReactNode;
  params: { tenantId: string; messengerId: string; hostConfig: string; locale: string };
}) {
  const config = useLauncherStore((state) => state.config);
  const hasFooter = useHasFooter();
  const { createPermalinkPath } = usePermalink({
    tenantId: params.tenantId,
    messengerId: params.messengerId,
    messengerToken: getMessengerToken(params.hostConfig),
  });

  return (
    <div
      className={clsx(
        "background",
        "flex grow flex-col",
        getBackgroundClasses(config?.settings?.themeSettings),
        "rounded-t-[inherit]",
        hasFooter ? undefined : "rounded-b-[inherit] [&>*:last-child]:rounded-b-[inherit]",
      )}
      style={getBackgroundStyles(createPermalinkPath, config?.settings?.themeSettings)}
    >
      {children}
    </div>
  );
}

export const getBackgroundClasses = (
  themeSettings?: MessengerThemeSettings,
): string | undefined => {
  const backgroundConfig = themeSettings?.background;

  if (!backgroundConfig?.name) {
    return undefined;
  }

  let classes = "";

  switch (backgroundConfig?.name) {
    case "color":
      classes = "bg-[--bgColor] dark:bg-[--bgColor]";
      break;
    case "gradient":
      classes = "bg-gradient-to-b from-[--bgStart] from-[200px] to-[--bgEnd]";
      break;
    case "image":
      if (backgroundConfig.bgImage?.file?.fileUuid) {
        classes = backgroundConfig.bgImage?.mode === "cover" ? "bg-cover" : "bg-repeat";
      }
      break;
    default:
      break;
  }

  return classes;
};

const getBackgroundStyles = (
  createPermalinkPath: (asset: string, fileUuid: string) => string,
  themeSettings?: MessengerThemeSettings,
): object | undefined => {
  const backgroundConfig = themeSettings?.background;

  if (!backgroundConfig?.name) {
    return undefined;
  }

  if (backgroundConfig?.name === "image" && backgroundConfig.bgImage?.file?.fileUuid) {
    const backgroundImageUrl = createPermalinkPath(
      "background-image",
      backgroundConfig.bgImage?.file?.fileUuid,
    );
    return { backgroundImage: 'url("' + backgroundImageUrl + '")' };
  }
};
